import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../v2/Modal";
import Button from "../v2/Button";
import { Field, Fieldset } from "@headlessui/react";
import FormDate from "../v2/Form/FormDate";
import FormInput from "../v2/Form/FormInput";
import FormSelect from "../v2/Form/FormSelect";
import {
  HOLIDAY_TYPE_OPTIONS,
  IS_PAID_OPTIONS,
} from "../../Constants/business";
import FormRadioGroup from "../v2/Form/FormRadioGroup";
import {
  addLocalNotification,
  clearCalendarHoliday,
  requestCreateCalendarHoliday,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../Context";
import FormError from "../v2/Form/FormError";

const HolidayModal = ({ open, onClose, selectedDate, calendar }) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [type, setType] = useState(null);
  const [isPaid, setIsPaid] = useState(IS_PAID_OPTIONS[0]);

  const [errors, setErrors] = useState({});

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    newCalendarHoliday,
    newCalendarHolidayLoading,
    newCalendarHolidayError,
  } = useBusinessTransactionState();

  //  Handle create success
  useEffect(() => {
    if (newCalendarHoliday?.id) {
      // Close modal
      handleClose();

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-holiday-${newCalendarHoliday?.id}`,
        type: "success",
        title: "common.success",
        body: "employees.holidays.notifications.createSuccess",
        showFull: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationDispatch, newCalendarHoliday?.id]);

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = t("employees.holidays.errors.nameRequired");
    }

    if (!type) {
      newErrors.type = t("employees.holidays.errors.typeRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const input = {
        calendarID: calendar?.id,
        name,
        type: type.value,
        isPaid: isPaid.value,
        date: selectedDate.toLocaleDateString("en-CA"),
      };
      requestCreateCalendarHoliday(dispatch, {
        input,
      });
    }
  };

  const handleClose = () => {
    setName("");
    setType(null);
    setIsPaid(IS_PAID_OPTIONS[0]);
    setErrors({});
    onClose();
    clearCalendarHoliday(dispatch);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="employees.holidays.modal.title.add"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={newCalendarHolidayLoading}
            loading={newCalendarHolidayLoading}
            type="submit"
            form="holiday-form"
            variant="primary"
          >
            {t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="holiday-form" onSubmit={handleSubmit}>
        <FormError error={newCalendarHolidayError} />
        <Fieldset disabled={newCalendarHolidayLoading} className="space-y-4">
          <Field>
            <FormDate
              label="employees.holidays.fields.date"
              id="date"
              name="date"
              selected={selectedDate}
              dateFormat="dd/MM/yyyy"
              disabled
            />
          </Field>
          <Field>
            <FormInput
              label="employees.holidays.fields.name"
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("employees.holidays.placeholders.name")}
              error={errors.name}
              required
            />
          </Field>
          <Field>
            <FormSelect
              label="employees.holidays.fields.type"
              id="type"
              name="type"
              value={type}
              onChange={(option) => setType(option)}
              options={HOLIDAY_TYPE_OPTIONS}
              placeholder={t("employees.holidays.placeholders.type")}
              error={errors.type}
              required
            />
          </Field>
          <Field>
            <FormRadioGroup
              label="employees.holidays.fields.isPaid"
              name="isPaid"
              value={isPaid}
              onChange={(value) => setIsPaid(value)}
              options={IS_PAID_OPTIONS}
            />
          </Field>
        </Fieldset>
      </form>
    </Modal>
  );
};

export default HolidayModal;
